import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../utils/api';
import { usePolling } from '../hooks/useApi';
import { BALANCE_TOKENS, BalanceToken, balanceTokenKeys, formatTokenAmount } from '../utils/balanceTokens';
import { BalanceNetworkBadges } from '../components/BalanceNetworkBadges';
import { formatHistoryTime } from '../utils/time';
import {
  formatCexSummary,
  formatDexConfirmTime,
  formatDexSubmitTime,
  formatDexSummary,
  formatDexTotalTime,
  formatDiagnosticsSummary,
  formatRecoverySummary,
  formatTotalsSummary,
  parseTradeDetails,
} from '../utils/tradeDetails';
import { formatSignedPercent, formatSignedUsd } from '../utils/numberFormat';
import type { RouteSpreadChartData, RouteSpreadTradeMarker } from '../utils/routeSpreadChart';

const BALANCE_NETWORK_TOKEN_QUERY = BALANCE_TOKENS.map(token => token.id).join(',');

function shortAddress(value: string): string {
  if (!value) return '-';
  if (value.length <= 14) return value;
  return value.slice(0, 5) + '...' + value.slice(-5);
}

function formatSpread(spread: number): string {
  return formatSignedPercent(spread);
}

const statusLabels: Record<string, string> = {
  success: 'успешно', partial: 'частично', failed: 'неуспешно', warning: 'внимание',
  below_threshold: 'ниже порога спреда', above_threshold: 'выше порога спреда',
};

const scanPhaseLabels: Record<string, string> = {
  balance: 'баланс',
  quote: 'котировка',
  spread: 'спред',
  done: 'готово',
  skipped: 'пропуск',
  error: 'ошибка',
};

const dexProviderLabels: Record<string, string> = {
  best: 'DEX: авто',
  dedust: 'DEX: DeDust',
  omniston: 'DEX: Omniston',
};

const routeExecutionModeLabels: Record<string, string> = {
  parallel: 'Обычный V2',
  dex_then_cex: 'DEX-first: DEX потом CEX',
  cex_then_dex: 'CEX-first: CEX потом DEX',
};

const routeSafetyModeLabels: Record<string, string> = {
  legacy: 'Старый',
  strict: 'Защита v2',
};

const routeModeLabels: Record<string, string> = {
  off: 'Выкл',
  swap: 'Свапать',
  watch: 'Смотреть',
};

const routeModeOrder = ['off', 'swap', 'watch'];

function routeLabel(route: any): string {
  return route.token + ' DEX ' + (route.direction === 'dex_to_cex' ? '>>' : '<<') + ' ' + route.cex.toUpperCase();
}

function routeExecutionModeOptions(route: any): Array<{ value: string; label: string }> {
  const options = [
    { value: 'parallel', label: routeExecutionModeLabels.parallel },
    { value: 'dex_then_cex', label: routeExecutionModeLabels.dex_then_cex },
  ];
  if (route.direction === 'cex_to_dex') {
    options.push({ value: 'cex_then_dex', label: routeExecutionModeLabels.cex_then_dex });
  }
  const current = route.executionMode || 'parallel';
  if (!options.some(option => option.value === current)) {
    options.push({ value: current, label: routeExecutionModeLabels[current] || current });
  }
  return options;
}

function observationLabel(observation: any): string {
  if (!observation) return '';
  const reason = String(observation.reason || '');
  if (reason.startsWith('error: ')) return reason.replace('error: ', '');
  if (reason.startsWith('balance: ')) return 'баланс: ' + reason.replace('balance: ', '');
  return statusLabels[reason] || reason;
}

function formatUsd(value: number | null): string {
  return formatSignedUsd(value);
}

const routeStatsPeriods = [
  { key: '1d', label: '24ч' },
  { key: '3d', label: '3д' },
  { key: '7d', label: '7д' },
] as const;

const routeChartPeriods = [
  { key: '24h', label: '24ч', hours: 24 },
  { key: '3d', label: '3д', hours: 72 },
  { key: '7d', label: '7д', hours: 168 },
] as const;

type RouteChartPeriodKey = typeof routeChartPeriods[number]['key'];

function emptyRouteStat() {
  return { tradeCount: 0, totalProfitUsd: 0 };
}

function routeStatTitle(stats: any): string {
  return routeStatsPeriods
    .map(period => {
      const bucket = stats?.[period.key] || emptyRouteStat();
      return period.label + ': сделок ' + bucket.tradeCount + ', итог ' + formatSignedUsd(bucket.totalProfitUsd);
    })
    .join(' · ');
}

function routeChartCacheKey(routeId: number, period: RouteChartPeriodKey): string {
  return routeId + ':' + period;
}

function getRouteChartPeriod(period: RouteChartPeriodKey) {
  return routeChartPeriods.find(item => item.key === period) || routeChartPeriods[0];
}

function formatChartTime(value: string): string {
  const date = new Date(String(value).replace(' ', 'T') + 'Z');
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'Europe/Moscow',
  });
}

function parseChartTimestamp(value: string): number | null {
  const parsed = Date.parse(String(value).replace(' ', 'T') + 'Z');
  return Number.isFinite(parsed) ? parsed : null;
}

function routeChartXTicks(period: RouteChartPeriodKey): Array<{ ratio: number; label: string }> {
  const periodConfig = getRouteChartPeriod(period);
  if (periodConfig.hours <= 24) {
    return [0, 6, 12, 18, 24].map(hour => ({
      ratio: hour / periodConfig.hours,
      label: hour + 'ч',
    }));
  }

  const days = periodConfig.hours / 24;
  return Array.from({ length: days + 1 }, (_, day) => ({
    ratio: day / days,
    label: day + 'д',
  }));
}

function formatChartAxisPercent(value: number): string {
  if (value === 0) return '0%';
  return (value > 0 ? '+' : '') + value + '%';
}

function finiteChartNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function formatCompactUsdValue(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '--';
  const abs = Math.abs(value);
  if (abs >= 1000000) return '$' + (value / 1000000).toFixed(2).replace(/\.?0+$/, '') + 'M';
  if (abs >= 1000) return '$' + (value / 1000).toFixed(2).replace(/\.?0+$/, '') + 'K';
  return '$' + value.toFixed(2).replace(/\.?0+$/, '');
}

function formatPlainPct(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '--';
  return value.toFixed(2).replace(/\.?0+$/, '') + '%';
}

function chartSpreadClass(value: number): string {
  if (value > 1) return 'blue';
  if (value >= 0.5) return 'green';
  if (value >= 0) return 'yellow';
  return 'negative';
}

function RouteSpreadChart({ data, loading, error, period }: { data?: RouteSpreadChartData; loading: boolean; error: string; period: RouteChartPeriodKey }) {
  const [selectedTradeId, setSelectedTradeId] = useState<number | null>(null);
  if (loading && !data) return <div className="route-chart-empty">Загрузка графика...</div>;
  if (error && !data) return <div className="route-chart-empty negative">Ошибка графика: {error}</div>;
  if (!data) return <div className="route-chart-empty">Нет данных за период</div>;

  const nowMs = parseChartTimestamp(data.to)!;
  const startMs = parseChartTimestamp(data.from)!;
  const spreadMin = -3;
  const spreadMax = 5;
  const yTicks = [5, 3, 1, 0, -1, -3];
  const xTicks = routeChartXTicks(period);
  const points = data.points
    .filter(row => finiteChartNumber(row.spreadPct) !== null)
    .map(row => ({ ...row, timestampMs: parseChartTimestamp(row.createdAt) }))
    .filter(row => row.timestampMs !== null && row.timestampMs >= startMs && row.timestampMs <= nowMs);
  const tradeMarkers = data.trades
    .filter(row => finiteChartNumber(row.spreadPct) !== null)
    .map(row => ({ ...row, timestampMs: parseChartTimestamp(row.createdAt) }))
    .filter(row => row.timestampMs !== null && row.timestampMs >= startMs && row.timestampMs <= nowMs);
  const selectedTrade = tradeMarkers.find(trade => trade.id === selectedTradeId);
  const tradeLabel = (trade: RouteSpreadTradeMarker) =>
    '#' + trade.id + ' · ' + formatChartTime(trade.createdAt) + ' UTC+3 · спред при запуске ' + formatSpread(trade.spreadPct) + ' · ' + (statusLabels[trade.status] || trade.status);

  if (points.length === 0 && tradeMarkers.length === 0) return <div className="route-chart-empty">Нет данных за период</div>;

  const width = 680;
  const height = 204;
  const padLeft = 44;
  const padRight = 18;
  const padTop = 14;
  const padBottom = 30;
  const plotWidth = width - padLeft - padRight;
  const plotHeight = height - padTop - padBottom;
  const values = (points.length ? points : tradeMarkers).map(point => Number(point.spreadPct));
  const actualMin = Math.min(...values);
  const actualMax = Math.max(...values);

  const xFor = (timestampMs: number) => {
    const ratio = Math.max(0, Math.min(1, (timestampMs - startMs) / (nowMs - startMs || 1)));
    return padLeft + ratio * plotWidth;
  };
  const yFor = (value: number) => {
    const clamped = Math.max(spreadMin, Math.min(spreadMax, value));
    return padTop + (spreadMax - clamped) * (plotHeight / (spreadMax - spreadMin));
  };
  const chartSegments = points.slice(1).map((point, index) => {
    const prev = points[index];
    const prevValue = Number(prev.spreadPct);
    const value = Number(point.spreadPct);
    const segmentValue = (prevValue + value) / 2;
    return {
      key: String(prev.createdAt) + ':' + String(point.createdAt) + ':' + index,
      x1: xFor(Number(prev.timestampMs)),
      y1: yFor(prevValue),
      x2: xFor(Number(point.timestampMs)),
      y2: yFor(value),
      className: chartSpreadClass(segmentValue),
    };
  });
  const volumePoints = points
    .map(point => ({ ...point, depthUsd: finiteChartNumber(point.cexDepthUsd) }))
    .filter(point => point.depthUsd !== null && point.depthUsd > 0);
  const maxDepthUsd = volumePoints.length > 0
    ? Math.max(...volumePoints.map(point => Number(point.depthUsd)))
    : 0;
  const barWidth = Math.max(1.5, Math.min(10, (plotWidth / Math.max(points.length, 1)) * 0.72));
  const last = points[points.length - 1];
  const lastValue = last ? Number(last.spreadPct) : null;
  const lineClass = lastValue === null ? 'neutral' : chartSpreadClass(lastValue);
  const firstTime = formatChartTime(points[0]?.createdAt || data.from);
  const lastTime = formatChartTime(last?.createdAt || data.to);
  const lastDepthUsd = finiteChartNumber(last?.cexDepthUsd);
  const lastCexImpact = finiteChartNumber(last?.cexVwapImpactPct);
  const lastDexImpact = finiteChartNumber(last?.dexPriceImpactPct);

  return (
    <div className="route-chart-body">
      <div className="route-chart-meta">
        <span>точек: {points.length} / {data.sampleCount}</span>
        <span>последний: <b className={lineClass}>{lastValue === null ? '--' : formatSpread(lastValue)}</b></span>
        <span>min: {formatSpread(actualMin)}</span>
        <span>max: {formatSpread(actualMax)}</span>
        <span>шкала: -3%..+5%</span>
        <span><span className="route-chart-trade-key" /> запусков: {tradeMarkers.length}</span>
        {maxDepthUsd > 0 && (
          <span><span className="route-chart-volume-key" /> CEX depth: {formatCompactUsdValue(lastDepthUsd)} / max {formatCompactUsdValue(maxDepthUsd)}</span>
        )}
        {lastCexImpact !== null && <span>CEX impact: {formatPlainPct(lastCexImpact)}</span>}
        {lastDexImpact !== null && <span>DEX impact: {formatPlainPct(lastDexImpact)}</span>}
        <span>{firstTime} - {lastTime} UTC+3</span>
        {error && <span className="negative">обновление: {error}</span>}
      </div>
      <svg className="route-chart-svg" viewBox={'0 0 ' + width + ' ' + height} role="img" aria-label="График спреда маршрута">
        {yTicks.map(tick => {
          const y = yFor(tick);
          return (
            <React.Fragment key={tick}>
              <line className={tick === 0 ? 'route-chart-zero' : 'route-chart-grid'} x1={padLeft} x2={width - padRight} y1={y} y2={y} />
              <text className="route-chart-axis" x={4} y={y + 4}>{formatChartAxisPercent(tick)}</text>
            </React.Fragment>
          );
        })}
        {xTicks.map(tick => {
          const x = padLeft + tick.ratio * plotWidth;
          return (
            <React.Fragment key={tick.label}>
              <line className="route-chart-grid route-chart-grid-vertical" x1={x} x2={x} y1={padTop} y2={height - padBottom} />
              <text className="route-chart-axis route-chart-axis-x" x={x} y={height - 10} textAnchor="middle">{tick.label}</text>
            </React.Fragment>
          );
        })}
        {maxDepthUsd > 0 && volumePoints.map((point, index) => {
          const depthUsd = Number(point.depthUsd);
          const ratio = Math.max(0, Math.min(1, depthUsd / maxDepthUsd));
          const barHeight = Math.max(1, ratio * plotHeight * 0.42);
          const x = xFor(Number(point.timestampMs)) - barWidth / 2;
          const y = height - padBottom - barHeight;
          return (
            <rect
              key={String(point.createdAt) + ':depth:' + index}
              className="route-chart-volume-bar"
              x={x}
              y={y}
              width={barWidth}
              height={barHeight}
              rx="1"
            >
              <title>
                CEX depth {formatCompactUsdValue(depthUsd)} · CEX impact {formatPlainPct(finiteChartNumber(point.cexVwapImpactPct))} · DEX impact {formatPlainPct(finiteChartNumber(point.dexPriceImpactPct))}
              </title>
            </rect>
          );
        })}
        {chartSegments.map(segment => (
          <line
            key={segment.key}
            className={'route-chart-segment ' + segment.className}
            x1={segment.x1}
            y1={segment.y1}
            x2={segment.x2}
            y2={segment.y2}
          />
        ))}
        {last && lastValue !== null && <circle className={'route-chart-last ' + lineClass} cx={xFor(Number(last.timestampMs))} cy={yFor(lastValue)} r="3.5" />}
        {tradeMarkers.map(trade => (
          <circle
            key={trade.id}
            className={'route-chart-trade' + (selectedTradeId === trade.id ? ' selected' : '')}
            cx={xFor(Number(trade.timestampMs))}
            cy={yFor(trade.spreadPct)}
            r="4.5"
            role="button"
            tabIndex={0}
            aria-label={'Сделка ' + tradeLabel(trade)}
            aria-pressed={selectedTradeId === trade.id}
            onClick={() => setSelectedTradeId(trade.id)}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setSelectedTradeId(trade.id);
              }
            }}
          ><title>{'Сделка ' + tradeLabel(trade)}</title></circle>
        ))}
      </svg>
      {selectedTrade && <div className="route-chart-trade-detail" role="status">Сделка {tradeLabel(selectedTrade)}</div>}
    </div>
  );
}

export function DashboardPage() {
  const { data: routes, refetch, error: routesErr, loading: routesLoading, failCount: routesFails } = usePolling(() => api.getRoutes(), 4000);
  const { data: routeStats } = usePolling(() => api.getRouteStats(), 15000);
  const { data: scannerStatus, refetch: refetchScanner } = usePolling(() => api.getScannerStatus(), 2000);
  const { data: balances, refetch: refetchBalances, error: balErr, failCount: balFails } = usePolling(() => api.getBalances(), 10000);
  const { data: balanceBaselines } = usePolling(() => api.getBalanceBaselines(), 60000);
  const { data: depositAddresses } = usePolling(() => api.getBalanceDepositAddresses(), 30000);
  const { data: evaaBscDepositAddresses } = usePolling(() => api.getBalanceDepositAddresses('EVAA', 'BSC'), 30000);
  const { data: networkAvailability } = usePolling(
    () => api.getBalanceNetworkAvailability(BALANCE_NETWORK_TOKEN_QUERY),
    60000,
    [BALANCE_NETWORK_TOKEN_QUERY]
  );
  const { data: trades, error: tradesErr, failCount: tradesFails } = usePolling(() => api.getTrades(20), 15000);
  const { data: observations } = usePolling(() => api.getObservations(), 5000);

  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<any>({});
  const [routeSaveStatus, setRouteSaveStatus] = useState<Record<number, 'saving' | 'saved' | 'error'>>({});
  const routeSaveTimersRef = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
  const routeSaveSeqRef = useRef<Record<number, number>>({});
  const routePendingPatchRef = useRef<Record<number, Record<string, unknown>>>({});
  const [chartRouteId, setChartRouteId] = useState<number | null>(null);
  const [chartPeriod, setChartPeriod] = useState<RouteChartPeriodKey>('24h');
  const [chartData, setChartData] = useState<Record<string, RouteSpreadChartData>>({});
  const [chartErrors, setChartErrors] = useState<Record<string, string>>({});
  const [chartLoadingKey, setChartLoadingKey] = useState<string | null>(null);
  const [baselineDraft, setBaselineDraft] = useState<Record<string, string>>({});
  const [baselineSaveStatus, setBaselineSaveStatus] = useState<Record<string, 'saving' | 'saved' | 'error'>>({});
  const baselineTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const baselineSeqRef = useRef<Record<string, number>>({});

  const platforms = [
    { id: 'ton', label: 'TON wallet', kind: 'main' },
    { id: 'gate', label: 'Gate', kind: 'main' },
    { id: 'mexc', label: 'MEXC', kind: 'main' },
    { id: 'binance', label: 'Binance', kind: 'main' },
    { id: 'bingx', label: 'BingX Spot', kind: 'main' },
    { id: 'bitmart', label: 'BitMart', kind: 'main' },
    { id: 'bybit', label: 'Bybit', kind: 'main' },
    { id: 'weex', label: 'WEEX', kind: 'main' },
  ] as const;
  const balanceTokens = BALANCE_TOKENS;
  const enabledRoutesCount = routes?.filter((route: any) => (route.mode || (route.enabled ? 'watch' : 'off')) !== 'off').length ?? scannerStatus?.enabledRoutes ?? 0;
  const totalRoutesCount = routes?.length ?? scannerStatus?.totalRoutes ?? 0;
  const activeRoute = scannerStatus?.activeRoute;
  const addressByPlatform = useMemo(() => {
    const map: Record<string, any> = {};
    for (const row of depositAddresses?.addresses || []) map[row.platform] = row;
    return map;
  }, [depositAddresses]);
  const bscAddressByPlatform = useMemo(
    () => Object.fromEntries((evaaBscDepositAddresses?.addresses || []).map((row: any) => [row.platform, row])),
    [evaaBscDepositAddresses]
  );
  const availabilityByCell = useMemo(() => {
    const map: Record<string, any[]> = {};
    for (const row of networkAvailability?.availability || []) {
      const key = row.platform + ':' + row.token;
      map[key] = [...(map[key] || []), row];
    }
    return map;
  }, [networkAvailability]);

  useEffect(() => {
    return () => {
      for (const timer of Object.values(routeSaveTimersRef.current)) clearTimeout(timer);
      for (const timer of Object.values(baselineTimersRef.current)) clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (!balanceBaselines) return;
    setBaselineDraft(prev => {
      const next = { ...prev };
      for (const row of balanceBaselines) next[row.token] = row.amount || '';
      return next;
    });
  }, [balanceBaselines]);

  useEffect(() => {
    if (!chartRouteId) return;
    const key = routeChartCacheKey(chartRouteId, chartPeriod);
    let cancelled = false;
    let refreshTimer: ReturnType<typeof setTimeout>;
    let controller: AbortController;
    const routeId = chartRouteId;

    async function refreshChart() {
      controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);
      setChartLoadingKey(key);
      try {
        const data = await api.getRouteSpreadChart(routeId, chartPeriod, controller.signal);
        if (cancelled) return;
        setChartData(prev => ({ ...prev, [key]: data }));
        setChartErrors(prev => ({ ...prev, [key]: '' }));
      } catch (err) {
        if (!cancelled) setChartErrors(prev => ({ ...prev, [key]: (err as Error).message }));
      } finally {
        clearTimeout(timeout);
        if (!cancelled) {
          setChartLoadingKey(current => current === key ? null : current);
          refreshTimer = setTimeout(refreshChart, 5000);
        }
      }
    }
    void refreshChart();

    return () => {
      cancelled = true;
      clearTimeout(refreshTimer);
      controller?.abort();
    };
  }, [chartRouteId, chartPeriod]);

  function getBal(platform: string, token: BalanceToken): string {
    const total = balances
      ?.filter((x: any) => x.platform === platform && balanceTokenKeys(token).includes(x.token))
      .reduce((sum: number, x: any) => sum + parseFloat(x.amount || '0'), 0) ?? 0;
    return total.toString();
  }

  function getTotal(token: BalanceToken): string {
    const mainPlatformIds = new Set(platforms.filter(platform => platform.kind === 'main').map(platform => platform.id));
    const mainTotal = balances
      ?.filter((x: any) => mainPlatformIds.has(x.platform) && balanceTokenKeys(token).includes(x.token))
      .reduce((sum: number, x: any) => sum + parseFloat(x.amount || '0'), 0) ?? 0;
    return mainTotal.toString();
  }

  function updateBaseline(token: BalanceToken, amount: string) {
    const tokenId = token.id;
    setBaselineDraft(prev => ({ ...prev, [tokenId]: amount }));
    setBaselineSaveStatus(prev => ({ ...prev, [tokenId]: 'saving' }));
    if (baselineTimersRef.current[tokenId]) clearTimeout(baselineTimersRef.current[tokenId]);
    const seq = (baselineSeqRef.current[tokenId] || 0) + 1;
    baselineSeqRef.current[tokenId] = seq;
    baselineTimersRef.current[tokenId] = setTimeout(async () => {
      try {
        await api.updateBalanceBaseline(tokenId, amount);
        if (baselineSeqRef.current[tokenId] === seq) {
          setBaselineSaveStatus(prev => ({ ...prev, [tokenId]: 'saved' }));
        }
      } catch {
        if (baselineSeqRef.current[tokenId] === seq) {
          setBaselineSaveStatus(prev => ({ ...prev, [tokenId]: 'error' }));
        }
      }
    }, 500);
  }

  function getNetworkAvailability(platform: string, token: BalanceToken): any[] {
    return availabilityByCell[platform + ':' + token.id] || [];
  }

  function getAddressHint(platform: string) {
    const row = addressByPlatform[platform];
    const ok = row?.status === 'ok' && row?.address;
    const bscRow = bscAddressByPlatform[platform];
    const showsBsc = platform === 'gate' || platform === 'mexc' || platform === 'bingx' || platform === 'bitmart' || platform === 'weex';
    const bscOk = showsBsc && bscRow?.status === 'ok' && bscRow?.address;
    return {
      text: ok ? shortAddress(row.address) : 'не задано',
      title: row?.address || row?.error || '',
      memo: ok && row.memo ? String(row.memo) : '',
      extra: showsBsc ? 'BSC: ' + (bscOk ? shortAddress(bscRow.address) : 'не задано') : '',
      extraTitle: showsBsc ? (bscRow?.address || bscRow?.error || '') : '',
      extraMissing: showsBsc ? !bscOk : false,
      missing: !ok,
    };
  }

  function latestObservation(routeId: number) {
    return observations?.find((item: any) => item.routeId === routeId);
  }

  function getRouteStats(routeId: number) {
    return routeStats?.[String(routeId)] || routeStats?.[routeId] || null;
  }

  async function handleModeChange(route: any, mode: string) {
    try {
      await api.updateRoute(route.id, { mode });
      refetch();
      refetchScanner();
    } catch {}
  }

  async function handleStartScanner() {
    try {
      await api.startScanner();
      refetchScanner();
    } catch {}
  }

  async function handleStopScanner() {
    try {
      await api.stopScanner();
      refetchScanner();
    } catch {}
  }

  async function handleSingleScan() {
    try {
      await api.triggerScan();
      refetchScanner();
    } catch {}
  }

  function startEdit(route: any) {
    setEditingId(route.id);
    setRouteSaveStatus(prev => ({ ...prev, [route.id]: 'saved' }));
    setEditForm({
      amountUsd: route.amountUsd,
      slippage: route.slippage,
      dexProvider: route.dexProvider || 'best',
      executionMode: route.executionMode || 'parallel',
      safetyMode: route.safetyMode || 'legacy',
      partialUnwindEnabled: route.partialUnwindEnabled || false,
      partialUnwindSlippage: route.partialUnwindSlippage ?? 1,
      partialUnwindMaxLossPct: route.partialUnwindMaxLossPct ?? 2,
      thresholdPct: route.thresholdPct,
      cooldownSec: route.cooldownSec,
    });
  }

  function handleRouteRowClick(route: any) {
    if (expandedId === route.id) {
      setExpandedId(null);
      if (editingId === route.id) setEditingId(null);
      return;
    }

    setExpandedId(route.id);
    startEdit(route);
  }

  function scheduleRouteSave(routeId: number, patch: Record<string, unknown>) {
    routePendingPatchRef.current[routeId] = { ...(routePendingPatchRef.current[routeId] || {}), ...patch };
    if (routeSaveTimersRef.current[routeId]) clearTimeout(routeSaveTimersRef.current[routeId]);
    const seq = (routeSaveSeqRef.current[routeId] || 0) + 1;
    routeSaveSeqRef.current[routeId] = seq;
    setRouteSaveStatus(prev => ({ ...prev, [routeId]: 'saving' }));

    routeSaveTimersRef.current[routeId] = setTimeout(async () => {
      const pendingPatch = routePendingPatchRef.current[routeId] || {};
      try {
        await api.updateRoute(routeId, pendingPatch);
        if (routeSaveSeqRef.current[routeId] !== seq) return;
        routePendingPatchRef.current[routeId] = {};
        setRouteSaveStatus(prev => ({ ...prev, [routeId]: 'saved' }));
        refetch();
        refetchScanner();
      } catch {
        if (routeSaveSeqRef.current[routeId] !== seq) return;
        setRouteSaveStatus(prev => ({ ...prev, [routeId]: 'error' }));
      }
    }, 400);
  }

  function updateRouteEdit(route: any, patch: Record<string, unknown>) {
    setEditForm((prev: any) => ({ ...prev, ...patch }));
    scheduleRouteSave(route.id, patch);
  }

  function routeSaveLabel(routeId: number): string {
    if (routeSaveStatus[routeId] === 'saving') return 'сохраняю...';
    if (routeSaveStatus[routeId] === 'saved') return 'сохранено';
    if (routeSaveStatus[routeId] === 'error') return 'ошибка сохранения';
    return 'автосохранение';
  }

  return (
    <div className="flex flex-col gap-3">

      {/* ===== БАЛАНСЫ ===== */}
      <div className="card" style={{ padding: '8px 12px' }}>
        <div className="flex items-center justify-between mb-1">
          <span className="section-title" style={{ margin: 0 }}>Балансы</span>
          <span className="balance-network-legend">↓ ввод · ↑ вывод</span>
        </div>
        <div className="balance-table-wrap">
          <table className="balance-table">
            <thead>
              <tr>
                <th scope="col">Токен</th>
                <th scope="col" className="balance-total-head">TOTAL</th>
                <th scope="col" className="balance-baseline-head">Было</th>
                {platforms.map(platform => {
                  const addressHint = getAddressHint(platform.id);
                  return (
                    <th key={platform.id} scope="col">
                      <span className="balance-platform-heading">
                        <span className="balance-platform-main">
                          <span className="balance-platform-name">
                            {platform.label}
                            {balFails > 3 && <span className="balance-warning" title={balErr || ''}>!</span>}
                          </span>
                          <span className={'balance-header-address' + (addressHint.missing ? ' missing' : '')} title={addressHint.title}>
                            ({addressHint.text})
                          </span>
                        </span>
                        {addressHint.memo && <span className="balance-header-memo">memo: {addressHint.memo}</span>}
                        {addressHint.extra && (
                          <span className={'balance-header-network' + (addressHint.extraMissing ? ' missing' : '')} title={addressHint.extraTitle}>
                            {addressHint.extra}
                          </span>
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {balanceTokens.map(token => (
                <tr key={token.id}>
                  <th scope="row">{token.label}</th>
                  <td className="balance-total-cell">
                    <span className="balance-total-amount">{formatTokenAmount(token, getTotal(token))}</span>
                  </td>
                  <td className="balance-baseline-cell">
                    <input
                      className="balance-baseline-input"
                      inputMode="decimal"
                      value={baselineDraft[token.id] || ''}
                      onChange={e => updateBaseline(token, e.target.value)}
                      title={baselineSaveStatus[token.id] === 'saving' ? 'сохраняю...' : baselineSaveStatus[token.id] === 'error' ? 'ошибка сохранения' : 'сохранено'}
                    />
                  </td>
                  {platforms.map(platform => (
                    <td key={platform.id}>
                      <span className="balance-cell">
                        <span className="balance-amount">{formatTokenAmount(token, getBal(platform.id, token))}</span>
                        <BalanceNetworkBadges networks={getNetworkAvailability(platform.id, token)} />
                      </span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ===== АРБИТРАЖ ===== */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="section-title" style={{ margin: 0 }}>Арбитраж</span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-secondary">
              Сканер: {scannerStatus?.running ? '✅' : '⏹'}
              {scannerStatus?.lastScanTime ? ' ' + new Date(scannerStatus.lastScanTime).toLocaleTimeString() : ''}
              {' · '}{enabledRoutesCount}/{totalRoutesCount} маршрутов
              {scannerStatus?.scanIntervalMs ? ' · ' + (scannerStatus.scanIntervalMs / 1000).toFixed(1).replace('.0', '') + 'с' : ''}
            </span>
            <button className="btn btn-sm btn-outline" style={{ fontSize: 10, padding: '2px 8px' }} onClick={handleStartScanner} disabled={scannerStatus?.running}>Старт</button>
            <button className="btn btn-sm btn-outline" style={{ fontSize: 10, padding: '2px 8px' }} onClick={handleStopScanner} disabled={!scannerStatus?.running}>Стоп</button>
            <button className="btn btn-sm btn-outline" style={{ fontSize: 10, padding: '2px 8px' }} onClick={handleSingleScan} disabled={enabledRoutesCount === 0}>Скан</button>
          </div>
        </div>
        {enabledRoutesCount === 0 && (
          <div className="text-xs text-secondary" style={{ padding: '4px 0' }}>
            Поставь хотя бы один маршрут в режим «Смотреть» или «Свапать», иначе сканеру нечего проверять.
          </div>
        )}
        {scannerStatus?.killSwitch && (
          <div className="text-xs text-secondary" style={{ padding: '4px 0' }}>
            Kill switch включён: dry-run сканирование можно запускать, торговые действия заблокированы.
          </div>
        )}
        {scannerStatus?.running && activeRoute && (
          <div className="scanner-active-line">
            <span className="scan-dot" />
            Проверяем: {routeLabel(activeRoute)}
            {' · '}{scanPhaseLabels[activeRoute.phase] || activeRoute.phase}
            {activeRoute.message ? ' · ' + activeRoute.message : ''}
          </div>
        )}

        {routesLoading && !routes ? (
          <div className="text-secondary text-xs">Загрузка маршрутов...</div>
        ) : routesFails > 3 && !routes ? (
          <div className="text-secondary text-xs">Ошибка соединения: {routesErr}</div>
        ) : !routes || routes.length === 0 ? (
          <div className="text-secondary text-xs">Нет маршрутов</div>
        ) : (
          routes.map((route: any) => {
            const observation = latestObservation(route.id);
            const hasError = observation?.reason?.startsWith('error:');
            const hasBalanceSkip = observation?.reason?.startsWith('balance:');
            const isScanning = activeRoute?.id === route.id && scannerStatus?.running;
            const routeMode = route.mode || (route.enabled ? 'watch' : 'off');
            const stats = getRouteStats(route.id);
            const isChartOpen = chartRouteId === route.id;
            const chartKey = routeChartCacheKey(route.id, chartPeriod);
            const chart = chartData[chartKey];
            const chartLoading = chartLoadingKey === chartKey;
            const spreadClass = !observation || hasError || hasBalanceSkip
              ? 'neutral'
              : observation.spreadPct > 0
                ? 'positive'
                : 'negative';

            return (
            <React.Fragment key={route.id}>
              <div
                className={'route-row route-mode-' + routeMode + (routeMode !== 'off' ? ' enabled' : '') + (isScanning ? ' scanning' : '')}
                style={{ padding: '5px 10px', cursor: 'pointer' }}
                onClick={() => handleRouteRowClick(route)}
              >
                <div className="route-mode-control" onClick={e => e.stopPropagation()}>
                  {routeModeOrder.map(mode => (
                    <button
                      key={mode}
                      className={'route-mode-btn' + (routeMode === mode ? ' active' : '')}
                      onClick={() => handleModeChange(route, mode)}
                    >
                      {routeModeLabels[mode]}
                    </button>
                  ))}
                </div>
                <span className="route-token" style={{ fontSize: 12, minWidth: 45 }}>{route.token}</span>
                <span className="route-direction" style={{ fontSize: 11, minWidth: 85 }}>
                  DEX {route.direction === 'dex_to_cex' ? '>>' : '<<'} {route.cex.toUpperCase()}
                </span>
                <span className={'route-spread ' + spreadClass} style={{ fontSize: 12, minWidth: 65 }}>
                  {observation && !hasError && !hasBalanceSkip ? formatSpread(observation.spreadPct) : '--'}
                </span>
                <button
                  className={'route-chart-btn' + (isChartOpen ? ' active' : '')}
                  onClick={e => {
                    e.stopPropagation();
                    setChartRouteId(current => current === route.id ? null : route.id);
                  }}
                >
                  График
                </button>
                <span className="route-stats" title={routeStatTitle(stats)}>
                  {routeStatsPeriods.map(period => {
                    const bucket = stats?.[period.key] || emptyRouteStat();
                    const profitClass = bucket.totalProfitUsd > 0
                      ? 'positive'
                      : bucket.totalProfitUsd < 0
                        ? 'negative'
                        : 'neutral';
                    return (
                      <span key={period.key} className="route-stat">
                        <span className="route-stat-period">{period.label}</span>
                        <span className="route-stat-count">{bucket.tradeCount}</span>
                        <span className={'route-stat-profit ' + profitClass}>{formatSignedUsd(bucket.totalProfitUsd)}</span>
                      </span>
                    );
                  })}
                </span>
                <span className="route-info" style={{ fontSize: 10 }} title={observation?.reason || ''}>
                  {routeModeLabels[routeMode]} · {routeExecutionModeLabels[route.executionMode || 'parallel'] || 'Обычный V2'} · {routeSafetyModeLabels[route.safetyMode || 'legacy']} · ${route.amountUsd} · {route.slippage}% slip · {dexProviderLabels[route.dexProvider || 'best'] || 'DEX: авто'} · спред ≥ {route.thresholdPct}% · {route.cooldownSec}c · возврат partial {route.partialUnwindEnabled ? 'вкл' : 'выкл'}
                  {isScanning && activeRoute ? ' · ' + (scanPhaseLabels[activeRoute.phase] || activeRoute.phase) : ''}
                  {observation ? ' · ' + observationLabel(observation) : ''}
                </span>
              </div>

              {isChartOpen && (
                <div className="route-chart-panel" onClick={e => e.stopPropagation()}>
                  <div className="route-chart-head">
                    <span className="route-chart-title">{routeLabel(route)} · история спреда</span>
                    <div className="route-chart-tabs">
                      {routeChartPeriods.map(period => (
                        <button
                          key={period.key}
                          className={'route-chart-tab' + (chartPeriod === period.key ? ' active' : '')}
                          onClick={() => setChartPeriod(period.key)}
                        >
                          {period.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <RouteSpreadChart key={chartKey} data={chart} loading={chartLoading} error={chartErrors[chartKey] || ''} period={chartPeriod} />
                </div>
              )}

              {expandedId === route.id && (
                <div className="route-details" style={{ padding: '6px 10px 8px' }}>
                  {editingId === route.id ? (
                    <div>
                      <div className="route-details-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
                        <div className="route-field">
                          <label style={{ fontSize: 9 }}>Сумма USDT</label>
                          <input type="number" min="0" step="1" value={editForm.amountUsd} onChange={e => updateRouteEdit(route, { amountUsd: parseFloat(e.target.value) || 0 })}
                            style={{ padding: '2px 4px', fontSize: 11 }} onPointerDown={e => e.stopPropagation()} />
                        </div>
                        <div className="route-field">
                          <label style={{ fontSize: 9 }}>Slippage %</label>
                          <input type="number" min="0" max="10" step="0.1" value={editForm.slippage} onChange={e => updateRouteEdit(route, { slippage: parseFloat(e.target.value) || 0 })}
                            style={{ padding: '2px 4px', fontSize: 11 }} onPointerDown={e => e.stopPropagation()} />
                        </div>
                        <div className="route-field">
                          <label style={{ fontSize: 9 }}>DEX роутер</label>
                          <select value={editForm.dexProvider || 'best'} onChange={e => updateRouteEdit(route, { dexProvider: e.target.value })}
                            style={{ padding: '2px 4px', fontSize: 11 }} onPointerDown={e => e.stopPropagation()}>
                            <option value="best">Авто: лучший output</option>
                            <option value="dedust">DeDust Router v2</option>
                            <option value="omniston">Omniston (STON.fi)</option>
                          </select>
                        </div>
                        <div className="route-field">
                          <label style={{ fontSize: 9 }}>Исполнение</label>
                          <select value={editForm.executionMode || 'parallel'} onChange={e => updateRouteEdit(route, { executionMode: e.target.value })}
                            style={{ padding: '2px 4px', fontSize: 11 }} onPointerDown={e => e.stopPropagation()}>
                            {routeExecutionModeOptions(route).map(option => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="route-field">
                          <label style={{ fontSize: 9 }}>Безопасность</label>
                          <div className="route-mode-control route-safety-control" onPointerDown={e => e.stopPropagation()}>
                            {['legacy', 'strict'].map(mode => (
                              <button
                                key={mode}
                                type="button"
                                className={'route-mode-btn' + ((editForm.safetyMode || 'legacy') === mode ? ' active' : '')}
                                onClick={e => {
                                  e.stopPropagation();
                                  updateRouteEdit(route, { safetyMode: mode });
                                }}
                              >
                                {routeSafetyModeLabels[mode]}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="route-field">
                          <label style={{ fontSize: 9 }}>Порог спреда %</label>
                          <input type="number" step="0.1" value={editForm.thresholdPct} onChange={e => updateRouteEdit(route, { thresholdPct: parseFloat(e.target.value) || 0 })}
                            style={{ padding: '2px 4px', fontSize: 11 }} onPointerDown={e => e.stopPropagation()} />
                        </div>
                        <div className="route-field">
                          <label style={{ fontSize: 9 }}>Кулдаун с</label>
                          <input type="number" value={editForm.cooldownSec} onChange={e => updateRouteEdit(route, { cooldownSec: parseInt(e.target.value) || 0 })}
                            style={{ padding: '2px 4px', fontSize: 11 }} onPointerDown={e => e.stopPropagation()} />
                        </div>
                        <div className="route-field">
                          <label style={{ fontSize: 9 }}>Ветки / путь</label>
                          <div className="text-mono text-xs" style={{ padding: '3px 0' }}>2 / 3</div>
                        </div>
                        <div className="route-field">
                          <label style={{ fontSize: 9 }}>Автовозврат partial</label>
                          <div className="route-mode-control route-safety-control" onPointerDown={e => e.stopPropagation()}>
                            {[false, true].map(enabled => (
                              <button
                                key={String(enabled)}
                                type="button"
                                className={'route-mode-btn' + (!!editForm.partialUnwindEnabled === enabled ? ' active' : '')}
                                onClick={e => {
                                  e.stopPropagation();
                                  updateRouteEdit(route, { partialUnwindEnabled: enabled });
                                }}
                              >
                                {enabled ? 'Вкл' : 'Выкл'}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="route-field">
                          <label style={{ fontSize: 9 }}>Slippage возврата %</label>
                          <input type="number" min="0" max="10" step="0.1" value={editForm.partialUnwindSlippage}
                            onChange={e => updateRouteEdit(route, { partialUnwindSlippage: parseFloat(e.target.value) || 0 })}
                            style={{ padding: '2px 4px', fontSize: 11 }} onPointerDown={e => e.stopPropagation()} />
                        </div>
                        <div className="route-field">
                          <label style={{ fontSize: 9 }}>Макс. убыток возврата %</label>
                          <input type="number" min="0" max="100" step="0.1" value={editForm.partialUnwindMaxLossPct}
                            onChange={e => updateRouteEdit(route, { partialUnwindMaxLossPct: parseFloat(e.target.value) || 0 })}
                            style={{ padding: '2px 4px', fontSize: 11 }} onPointerDown={e => e.stopPropagation()} />
                        </div>
                      </div>
                      <div className="flex gap-1 mt-1">
                        <span className="text-xs text-secondary">{routeSaveLabel(route.id)}</span>
                        <button className="btn btn-sm btn-outline" style={{ fontSize: 10, padding: '2px 8px' }} onClick={(e) => { e.stopPropagation(); setEditingId(null); }}>Готово</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex gap-3 text-xs">
                        <span className="text-muted">Режим: {routeModeLabels[routeMode]}</span>
                        <span className="text-muted">{routeExecutionModeLabels[route.executionMode || 'parallel'] || 'Обычный V2'}</span>
                        <span className="text-muted">{routeSafetyModeLabels[route.safetyMode || 'legacy']}</span>
                        <span className="text-muted">${route.amountUsd} · {route.slippage}% slippage</span>
                        <span className="text-muted">{dexProviderLabels[route.dexProvider || 'best'] || 'DEX: авто'}</span>
                        <span className="text-muted">2/3 ветки/пути</span>
                        <span className="text-muted">спред ≥ {route.thresholdPct}% · {route.cooldownSec}с кулдаун</span>
                        <span className="text-muted">возврат partial: {route.partialUnwindEnabled ? 'вкл' : 'выкл'} · {route.partialUnwindSlippage ?? 1}% slip · лимит {route.partialUnwindMaxLossPct ?? 2}%</span>
                      </div>
                      <button className="btn btn-sm btn-outline" style={{ fontSize: 10, padding: '2px 8px' }} onClick={(e) => { e.stopPropagation(); startEdit(route); }}>Изменить</button>
                    </div>
                  )}
                </div>
              )}
            </React.Fragment>
          );
          })
        )}
      </div>

      {/* ===== ИСТОРИЯ ===== */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="section-title" style={{ margin: 0 }}>История</span>
          <span className="text-xs text-muted">последние сделки</span>
        </div>

        <div style={{ overflowX: 'auto' }}>
          {tradesFails > 3 && <div className="text-xs text-secondary" style={{ padding: '6px' }}>Нет соединения: {tradesErr}</div>}
          {tradesFails <= 3 && (
            <table className="table">
              <thead>
                <tr>
                  <th>ID</th><th>Токен</th><th>Маршрут</th><th>Размер</th>
                  <th>Ожидали</th><th>Итог</th><th>Статус</th>
                  <th>Дельты</th><th>Диагн.</th><th>DEX нога</th><th>Возврат</th><th>CEX нога</th><th>DEX send</th><th>DEX confirm</th><th>DEX total</th><th>CEX время</th>
                  <th>Причина</th><th>Когда</th>
                </tr>
              </thead>
              <tbody>
                {trades?.map((t: any) => {
                  const details = parseTradeDetails(t);
                  return (
                    <tr key={t.id}>
                      <td className="text-muted text-xs">{t.id}</td>
                      <td className="route-token">{t.token}</td>
                      <td className="text-xs">DEX {t.direction === 'dex_to_cex' ? '->' : '<-'} {t.cex}</td>
                      <td className="text-mono">${t.amountUsd}</td>
                      <td className="text-mono">{formatUsd(t.expectedProfitUsd)}</td>
                      <td className={'text-mono ' + (t.actualProfitUsd !== null && t.actualProfitUsd >= 0 ? 'spread positive' : 'spread negative')}>
                        {formatUsd(t.actualProfitUsd)}
                      </td>
                      <td>
                        <span className={'badge ' + (
                          details?.recovery?.status === 'completed' ? 'badge-warning' :
                          t.status === 'success' ? 'badge-success' :
                          t.status === 'partial' ? 'badge-warning' :
                          t.status === 'failed' ? 'badge-danger' : 'badge-neutral'
                        )}>{details?.recovery?.status === 'completed' ? 'возвращено' : (statusLabels[t.status] || t.status)}</span>
                      </td>
                      <td className="text-xs text-mono" style={{ minWidth: 220 }}>{details ? formatTotalsSummary(details) : '-'}</td>
                      <td className="text-xs text-mono" style={{ minWidth: 260 }}>{formatDiagnosticsSummary(details) || '-'}</td>
                      <td className="text-xs">{details ? formatDexSummary(details) : (t.dexLeg || '-')}</td>
                      <td className="text-xs" style={{ minWidth: 260 }}>{formatRecoverySummary(details) || '-'}</td>
                      <td className="text-xs">{details ? formatCexSummary(details) : (t.cexLeg || '-')}</td>
                      <td className="text-xs">{formatDexSubmitTime(details)}</td>
                      <td className="text-xs">{formatDexConfirmTime(details)}</td>
                      <td className="text-xs">{formatDexTotalTime(details, t.dexTimeMs)}</td>
                      <td className="text-xs">{t.cexTimeMs !== null ? (t.cexTimeMs / 1000).toFixed(1) + 'с' : '-'}</td>
                      <td className="text-xs" style={{ color: 'var(--text-muted)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.reason || '-'}</td>
                      <td className="text-xs text-muted" title={t.createdAt}>{formatHistoryTime(t.createdAt)}</td>
                    </tr>
                  );
                })}
                {trades && trades.length === 0 && (
                  <tr><td colSpan={18} className="text-secondary text-xs">Сделок пока нет</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

    </div>
  );
}
